import { useState, useEffect, useRef, useCallback } from 'react';
import { Bot, User, Loader2, Pencil } from 'lucide-react';
import ToolCallCard from './ToolCallCard';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MarkdownTable } from '../data-explorer/MarkdownTable';
import { HtmlBlock } from './HtmlBlock';
import { ChatInput } from './ChatInput';
import { normalizeMarkdown } from '../../utils/markdownUtils';
import { chatTheme as t } from '../../theme/chat';

/**
 * Shared chat component used by Data Explorer, Financial Summary, and Data Views.
 *
 * Props:
 *  - api                 — API service object with: streamChat, getModels, getThread, getThreads (optional)
 *  - agentName           — display name (optional, shown in empty state)
 *  - threadId            — external thread ID to resume
 *  - onThreadCreated     — callback(threadId) when new thread is created
 *
 *  Company/Model:
 *  - selectedCompanies   — array of {rf_id, company_name, source_type}
 *  - onSelectCompany     — callback(realm)
 *  - onRemoveCompany     — callback(rfId)
 *
 *  Context:
 *  - buildContext        — (companies, threadId) => context object for request body
 *                          Default: builds selected_rf_ids + selected_companies
 *
 *  Custom SSE events:
 *  - onCustomEvent       — (eventType, data, updateLast) => boolean
 *                          Return true if handled, false to use default handling
 *
 *  Custom segment rendering:
 *  - renderCustomSegment — (seg, j, handleSend) => ReactNode | null
 *                          Return null to skip, component to render
 *
 *  UI customization:
 *  - emptyState          — ReactNode for empty state content
 *  - header              — ReactNode for header bar (above messages)
 *  - placeholder         — input placeholder text
 *  - className           — additional CSS classes for the container
 *  - compact             — boolean, use smaller text (xs vs sm)
 */
export function AgentChat({
  api,
  agentName,
  threadId: externalThreadId,
  onThreadCreated,
  selectedCompanies = [],
  onSelectCompany,
  onRemoveCompany,
  buildContext,
  onCustomEvent,
  renderCustomSegment,
  emptyState,
  header,
  placeholder,
  className = '',
  compact = false,
  enableMessageEdit = false,
  onBeforeEdit,
}) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [threadId, setThreadId] = useState(externalThreadId);
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [usage, setUsage] = useState(null);

  const [editingIndex, setEditingIndex] = useState(null);
  const [editText, setEditText] = useState('');

  const messagesEndRef = useRef(null);
  const abortRef = useRef(null);
  const sendingRef = useRef(false);
  const justStreamedThreadRef = useRef(null);

  const textSize = compact ? 'text-xs' : 'text-sm';
  const proseClasses = compact ? t.proseCompact : t.prose;

  // Fetch models
  useEffect(() => {
    api.getModels?.().then(d => {
      const m = d.models || [];
      setModels(m);
      if (m.length > 0 && !selectedModel) setSelectedModel(m[0].id);
    }).catch(() => {});
  }, [api]);

  // Sync thread from parent
  useEffect(() => {
    setThreadId(externalThreadId);
    if (sendingRef.current) return;
    if (externalThreadId && externalThreadId === justStreamedThreadRef.current) {
      justStreamedThreadRef.current = null;
      return;
    }
    if (externalThreadId) {
      api.getThread?.(externalThreadId).then(data => {
        if (sendingRef.current) return;
        setMessages((data.messages || []).map(m => {
          const segs = [];
          if (m.toolCalls?.length) m.toolCalls.forEach(tc => segs.push({ type: 'tool_call', tool: tc.tool, status: 'done' }));
          if (m.content) segs.push({ type: 'text', content: m.content });
          return { role: m.role, content: m.content || '', toolCalls: m.toolCalls || [], segments: segs };
        }));
      }).catch(() => {
        if (!sendingRef.current) setMessages([]);
      });
    } else {
      setMessages([]);
    }
  }, [externalThreadId, api]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  const updateLast = (updater) => {
    setMessages(prev => {
      const last = prev[prev.length - 1];
      if (last?.role === 'assistant') {
        return [...prev.slice(0, -1), updater(last)];
      }
      return prev;
    });
  };

  // ── Message editing (restore/fork) ───────────────────────────────────
  const handleSendRef = useRef(null);

  const handleEditMessage = useCallback((index) => {
    if (loading || sendingRef.current) return;
    const msg = messages[index];
    if (msg?.role !== 'user') return;
    setEditText(typeof msg.content === 'string' ? msg.content : '');
    setEditingIndex(index);
  }, [loading, messages]);

  const handleCancelEdit = useCallback(() => {
    setEditingIndex(null);
    setEditText('');
  }, []);

  const handleSubmitEdit = useCallback(() => {
    if (!editText.trim() || loading || sendingRef.current) return;
    const text = editText.trim();
    const editIdx = editingIndex;
    // Count which user message this is (0-based) for the backend's edit_index
    let userMsgIndex = 0;
    for (let j = 0; j < editIdx; j++) {
      if (messages[j]?.role === 'user') userMsgIndex++;
    }
    // Notify parent before edit (e.g. to clear workspace)
    onBeforeEdit?.(editIdx);
    // Truncate messages from edit point
    setMessages(prev => prev.slice(0, editIdx));
    setEditingIndex(null);
    setEditText('');
    setTimeout(() => handleSendRef.current?.(text, null, userMsgIndex), 50);
  }, [editText, editingIndex, loading, messages, onBeforeEdit]);

  const handleEditKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitEdit();
    }
    if (e.key === 'Escape') handleCancelEdit();
  }, [handleSubmitEdit, handleCancelEdit]);

  const handleSend = useCallback(async (text, images, editIndex) => {
    if (sendingRef.current) return;
    sendingRef.current = true;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setUsage(null);
    setMessages(prev => [...prev,
      { role: 'user', content: text, images: images || [], segments: [] },
      { role: 'assistant', content: '', toolCalls: [], segments: [] },
    ]);

    // Build context
    const context = buildContext
      ? buildContext(selectedCompanies, threadId)
      : selectedCompanies.length > 0 ? {
          selected_rf_ids: selectedCompanies.map(c => c.rf_id),
          selected_companies: selectedCompanies.map(c => ({
            rf_id: c.rf_id, name: c.company_name, source_type: c.source_type,
          })),
        } : undefined;

    const body = {
      message: text,
      user_id: 'admin',
      thread_id: threadId,
      model: selectedModel || undefined,
      images: images?.length > 0 ? images.map(img => ({ base64: img.base64, mime_type: img.mime_type })) : undefined,
      context,
    };
    if (editIndex !== undefined) {
      body.edit_index = editIndex;
    }

    try {
      const response = await api.streamChat(body, controller.signal);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentEvent = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event:')) {
            currentEvent = line.slice(6).trim();
          } else if (line.startsWith('data:') && currentEvent) {
            const eventType = currentEvent;
            currentEvent = null;
            try {
              const data = JSON.parse(line.slice(5).trim());

              // Let parent handle custom events first
              if (onCustomEvent?.(eventType, data, updateLast)) continue;

              switch (eventType) {
                case 'thread_id':
                  setThreadId(data.thread_id);
                  abortRef.current._newThreadId = data.thread_id;
                  break;
                case 'token':
                  updateLast(last => {
                    const segs = [...(last.segments || [])];
                    const lastSeg = segs[segs.length - 1];
                    if (lastSeg?.type === 'text') {
                      segs[segs.length - 1] = { ...lastSeg, content: lastSeg.content + data.content };
                    } else {
                      segs.push({ type: 'text', content: data.content });
                    }
                    return { ...last, content: last.content + data.content, segments: segs };
                  });
                  break;
                case 'tool_call':
                  updateLast(last => {
                    const existing = last.toolCalls || [];
                    if (existing.some(t => t.tool === data.tool && t.status === 'running')) return last;
                    const segs = [...(last.segments || [])];
                    segs.push({ type: 'tool_call', tool: data.tool, status: 'running' });
                    return { ...last, toolCalls: [...existing, { tool: data.tool, status: 'running' }], segments: segs };
                  });
                  break;
                case 'tool_result':
                  updateLast(last => {
                    const newToolCalls = (last.toolCalls || []).map(tc =>
                      tc.tool === data.tool && tc.status === 'running' ? { ...tc, status: 'done' } : tc
                    );
                    const segs = [...(last.segments || [])];
                    for (let i = segs.length - 1; i >= 0; i--) {
                      if (segs[i].type === 'tool_call' && segs[i].tool === data.tool && segs[i].status === 'running') {
                        segs[i] = { ...segs[i], status: 'done' };
                        break;
                      }
                    }
                    // Parse result for charts/tables
                    if (data.result) {
                      try {
                        const parsed = JSON.parse(data.result);
                        if (parsed.chart) {
                          segs.push({ type: 'chart', option: parsed.chart });
                        } else {
                          const inner = parsed.data || parsed;
                          if (inner.columns && inner.rows && inner.rows.length > 0 && inner.columns.length > 1) {
                            segs.push({ type: 'table', columns: inner.columns, rows: inner.rows, executionTimeMs: inner.execution_time_ms });
                          }
                        }
                      } catch {}
                    }
                    return { ...last, toolCalls: newToolCalls, segments: segs };
                  });
                  break;
                case 'chart':
                  if (data.option) {
                    updateLast(last => {
                      const segs = [...(last.segments || [])];
                      const hasChart = segs.some(s => s.type === 'chart' && JSON.stringify(s.option) === JSON.stringify(data.option));
                      if (!hasChart) segs.push({ type: 'chart', option: data.option });
                      return { ...last, segments: segs };
                    });
                  }
                  break;
                case 'html_block':
                  updateLast(last => {
                    const segs = [...(last.segments || [])];
                    segs.push({ type: 'html_block', html: data.data?.html || data.html, id: data.data?.id || data.id });
                    return { ...last, segments: segs };
                  });
                  break;
                case 'spreadsheet_url':
                  updateLast(last => {
                    const segs = [...(last.segments || [])];
                    segs.push({ type: 'spreadsheet_url', url: data.url, spreadsheetId: data.spreadsheet_id });
                    return { ...last, segments: segs };
                  });
                  break;
                case 'usage':
                  setUsage(data);
                  break;
                case 'error':
                  updateLast(last => {
                    const segs = [...(last.segments || [])];
                    segs.push({ type: 'text', content: `\nError: ${data.message}` });
                    return { ...last, content: last.content + `\nError: ${data.message}`, segments: segs };
                  });
                  break;
                case 'done':
                  break;
                default:
                  break;
              }
            } catch {}
          } else if (line.trim() === '') {
            currentEvent = null;
          }
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        sendingRef.current = false;
        return;
      }
      updateLast(last => ({ ...last, content: `Error: ${err.message}`, segments: [{ type: 'text', content: `Error: ${err.message}` }] }));
    }
    sendingRef.current = false;
    setLoading(false);
    const newTid = abortRef.current?._newThreadId;
    if (newTid) {
      justStreamedThreadRef.current = newTid;
      if (onThreadCreated) onThreadCreated(newTid);
    }
  }, [threadId, selectedModel, selectedCompanies, onThreadCreated, api, buildContext, onCustomEvent]);

  handleSendRef.current = handleSend;

  // Lazy-load heavy components
  const ChartRenderer = useRef(null);
  const QueryResultTable = useRef(null);

  const getChartRenderer = () => {
    if (!ChartRenderer.current) {
      try { ChartRenderer.current = require('../data-explorer/ChartRenderer').ChartRenderer; } catch { ChartRenderer.current = () => null; }
    }
    return ChartRenderer.current;
  };
  const getQueryResultTable = () => {
    if (!QueryResultTable.current) {
      try { QueryResultTable.current = require('../data-explorer/QueryResultTable').QueryResultTable; } catch { QueryResultTable.current = () => null; }
    }
    return QueryResultTable.current;
  };

  const renderSegment = (seg, j) => {
    // Let parent render custom segments first
    if (renderCustomSegment) {
      const custom = renderCustomSegment(seg, j, handleSend);
      if (custom !== null && custom !== undefined) return custom;
    }

    if (seg.type === 'tool_call') {
      return <ToolCallCard key={j} tool={seg.tool} status={seg.status} result={seg.result} />;
    }
    if (seg.type === 'table') {
      const Table = getQueryResultTable();
      return (
        <div key={j} className="my-3">
          <Table columns={seg.columns} rows={seg.rows} executionTimeMs={seg.executionTimeMs} />
        </div>
      );
    }
    if (seg.type === 'chart') {
      const Chart = getChartRenderer();
      return (
        <div key={j} className="my-3">
          <Chart option={seg.option} />
        </div>
      );
    }
    if (seg.type === 'html_block' && seg.html) {
      return (
        <div key={j} className="my-2">
          <HtmlBlock html={seg.html} id={seg.id} onFormSubmit={(text) => handleSend(text)} />
        </div>
      );
    }
    if (seg.type === 'spreadsheet_url' && seg.url) {
      return (
        <div key={j} className="my-3 flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
          <svg className="h-4 w-4 text-emerald-600 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
          <a href={seg.url} target="_blank" rel="noopener noreferrer"
            className="text-sm text-emerald-700 hover:text-emerald-900 font-medium underline underline-offset-2">
            Open Google Sheet
          </a>
        </div>
      );
    }
    if (seg.type === 'text' && seg.content) {
      return (
        <div key={j} className={`${proseClasses} my-1`}
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ table: MarkdownTable }}>
            {normalizeMarkdown(seg.content)}
          </ReactMarkdown>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={`flex flex-col h-full w-full ${t.bg} ${className}`}>
      {/* Header */}
      {header}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center px-2">
            {(typeof emptyState === 'function' ? emptyState(handleSend) : emptyState) || (
              <>
                <Bot size={28} className="text-accent/30 mb-3" />
                <p className={`${textSize} text-zinc-400`}>{agentName ? `Ask ${agentName} anything.` : 'How can I help you?'}</p>
              </>
            )}
          </div>
        )}

        {messages.map((msg, i) => {
          const isToolOnly = msg.role === 'assistant' && msg.segments?.length > 0 && msg.segments.every(s => s.type === 'tool_call');
          const prevIsAssistant = i > 0 && messages[i - 1].role === 'assistant';
          const collapseAvatar = isToolOnly && prevIsAssistant;
          return (
          <div key={i} className={`group relative flex gap-2 ${msg.role === 'user' ? 'justify-end' : ''} ${collapseAvatar ? '-mt-2' : ''}`}>
            {msg.role === 'assistant' && (
              collapseAvatar
                ? <div className="w-6 flex-shrink-0" />
                : <div className={`w-6 h-6 rounded-full ${t.botIcon} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                    <Bot size={12} />
                  </div>
            )}
            <div className={msg.role === 'user'
              ? `${enableMessageEdit && editingIndex === i ? 'flex-1' : 'max-w-[85%]'} ${editingIndex === i ? '' : `${t.userBubble}`} ${textSize}`
              : `flex-1 min-w-0 ${t.assistantText} ${textSize} leading-relaxed`
            }>
              {msg.role === 'assistant' && (msg.segments || []).map((seg, j) => renderSegment(seg, j))}
              {msg.role === 'user' && enableMessageEdit && editingIndex === i ? (
                <div className="rounded-lg border-2 border-amber-400 bg-white overflow-hidden w-full">
                  <textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    onKeyDown={handleEditKeyDown}
                    rows={Math.min(6, Math.max(2, editText.split('\n').length))}
                    autoFocus
                    className={`w-full px-3 py-2 ${textSize} resize-none focus:outline-none`}
                  />
                  <div className="flex items-center justify-end gap-2 px-2 pb-2">
                    <button onClick={handleCancelEdit}
                      className="px-2.5 py-1 text-xs text-zinc-500 hover:text-zinc-700 rounded hover:bg-zinc-100">
                      Cancel
                    </button>
                    <button onClick={handleSubmitEdit} disabled={!editText.trim()}
                      className="px-2.5 py-1 text-xs font-medium text-white bg-amber-500 rounded hover:bg-amber-600 disabled:opacity-50">
                      Send
                    </button>
                  </div>
                </div>
              ) : msg.role === 'user' && (
                <div>
                  {msg.images?.length > 0 && (
                    <div className="flex gap-1.5 mb-1.5 flex-wrap">
                      {msg.images.map((img, idx) => (
                        <img key={idx} src={`data:${img.mime_type};base64,${img.base64}`}
                             alt="attached" className="max-w-[100px] max-h-[60px] rounded-md border border-zinc-200" />
                      ))}
                    </div>
                  )}
                  {msg.content}
                </div>
              )}
              {msg.role === 'assistant' && loading && i === messages.length - 1 && !(msg.segments?.length) && (
                <div className="flex items-center gap-1.5 h-6">
                  <Loader2 size={12} className={`animate-spin ${t.spinner}`} />
                  <span className={`text-[11px] ${t.thinkingText}`}>Thinking...</span>
                </div>
              )}
            </div>
            {msg.role === 'user' && enableMessageEdit && editingIndex !== i && !loading && (
              <button
                onClick={() => handleEditMessage(i)}
                className="opacity-0 group-hover:opacity-100 transition-opacity w-5 h-5 flex items-center justify-center rounded hover:bg-zinc-200 flex-shrink-0 mt-0.5"
                title="Edit message"
              >
                <Pencil size={10} className="text-zinc-400" />
              </button>
            )}
            {msg.role === 'user' && (
              <div className={`w-6 h-6 rounded-full ${t.userIcon} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                <User size={12} />
              </div>
            )}
          </div>
        );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Usage */}
      {usage && (
        <div className={`text-[10px] ${t.usageText} text-center py-0.5`}>
          {(usage.input_tokens + usage.output_tokens).toLocaleString()} tokens
        </div>
      )}

      {/* Input */}
      <ChatInput
        onSend={handleSend}
        loading={loading}
        selectedModel={selectedModel}
        placeholder={placeholder}
        selectedCompanies={selectedCompanies}
        onSelectCompany={onSelectCompany}
        onRemoveCompany={onRemoveCompany}
        models={models}
        onModelChange={setSelectedModel}
      />
    </div>
  );
}
